import * as React from 'react';
import * as am4charts from '@amcharts/amcharts4/charts';

import { useCollectItems } from './useCollectItems';

/** To be called on a collection of ready charts via useCollectItems */
const alignCharts = <T extends am4charts.XYChart>(chartsToSync: T[]) => {
  // https://www.amcharts.com/docs/v4/tutorials/syncing-width-of-the-vertical-axes-across-multiple-charts/
  // syncAxisWidth is registered as a PERSISTENT 'ready' listener on every synced chart, not a
  // one-off calculation - amCharts re-fires 'ready' (and so re-runs this) every time any synced
  // chart's own data changes and it re-validates, e.g. on every subsequent series clear+recreate
  // from a data refresh, not just at initial mount. appliedMaxWidth tracks the largest width ever
  // applied across those re-runs, so the shared width can only grow, never shrink: without this, a
  // later re-run catching one chart's axis.measuredWidth mid-relayout (or a sibling whose own axis
  // is simply narrower, e.g. "0"/"0.5"/"1.0" vs "100%") could re-narrow every chart in the group
  // below a wider member's genuine requirement, even after it had previously been sized correctly.
  let appliedMaxWidth = 0;

  function syncAxisWidth() {
    let maxWidth = appliedMaxWidth;
    let allReady = true;

    chartsToSync.forEach((chart) => {
      if (chart.isReady()) {
        const axis = chart.yAxes.getIndex(0);
        maxWidth = maxWidth > axis.measuredWidth ? maxWidth : axis.measuredWidth;
      } else {
        allReady = false;
      }
    });

    if (allReady) {
      appliedMaxWidth = maxWidth;
      chartsToSync.forEach((chart) => {
        const axis = chart.yAxes.getIndex(0);
        axis.width = maxWidth;
      });
    }
  }

  chartsToSync.forEach((chart) => { chart.events.on("ready", syncAxisWidth); });
};

export type AlignChartsReturn<T extends am4charts.XYChart> = {
  addChart: (item: T) => T,
  clearCharts: () => void,
  charts: T[]
};

/** Aligns y axes of collected charts */
/* TODO: retest using testing-library hooks */
/* istanbul ignore next */
export const useAlignCharts = <T extends am4charts.XYChart>(chartCount: number): AlignChartsReturn<T> => {
  // Memoized so useCollectItems' own effect (which lists this callback as one of its dependencies)
  // only re-runs when the collected items themselves actually change - not on every unrelated
  // re-render of the consuming component. An inline, unmemoized callback would re-run that effect
  // (and so call alignCharts, which registers a NEW persistent 'ready' listener on every chart -
  // see alignCharts' own comment on why that listener is persistent rather than one-off) on every
  // such re-render, leaking an ever-growing number of duplicate listeners over the component's
  // lifetime instead of the single set registered once the chart count is first reached.
  const onCollected = React.useCallback((items: T[]) => dependencies.alignCharts(items), []);
  const { items, addItem, clearItems } = useCollectItems<T>(chartCount, onCollected);
  const addChart = React.useMemo(() => addItem, [addItem]);
  const clearCharts = React.useMemo(() => clearItems, [clearItems]);
  return { charts: items, addChart, clearCharts };
};

export const dependencies = {
  alignCharts
};
